import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { Card } from '../../components/ui/Card';
import { Select } from '../../components/ui/Select';
import {
  TrendingUp,
  Download,
  DollarSign,
  Users,
  FileText,
  BarChart3,
  PieChart,
} from 'lucide-react';

type ReportTab = 'revenue' | 'customers' | 'charges' | 'export';

export function SupplierReports() {
  const [activeTab, setActiveTab] = useState<ReportTab>('revenue');
  const [period, setPeriod] = useState('30');

  // Fetch revenue summary
  const { data: revenueData, isLoading: revenueLoading } = useQuery({
    queryKey: ['supplier', 'reports', 'revenue', period],
    queryFn: async () => {
      const response = await api.get('/banking/supplier/reports/revenue', {
        params: { days: period },
      });
      return response.data;
    },
    enabled: activeTab === 'revenue',
  });

  // Fetch top customers
  const { data: customersData, isLoading: customersLoading } = useQuery({
    queryKey: ['supplier', 'reports', 'customers', period],
    queryFn: async () => {
      const response = await api.get('/banking/supplier/reports/top-customers', {
        params: { days: period },
      });
      return response.data;
    },
    enabled: activeTab === 'customers',
  });

  // Fetch charge stats
  const { data: chargeStats, isLoading: chargeLoading } = useQuery({
    queryKey: ['supplier', 'reports', 'charges', period],
    queryFn: async () => {
      const response = await api.get('/banking/supplier/reports/charge-stats', {
        params: { days: period },
      });
      return response.data;
    },
    enabled: activeTab === 'charges',
  });


  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value / 100);
  };

  const handleExport = async (format: 'pdf' | 'csv') => {
    try {
      const response = await api.get('/banking/supplier/reports/export', {
        params: { format, days: period },
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `relatorio-${period}dias.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Export error:', error);
    }
  };

  const tabs = [
    { id: 'revenue' as ReportTab, label: 'Receita', icon: <TrendingUp size={18} /> },
    { id: 'customers' as ReportTab, label: 'Clientes', icon: <Users size={18} /> },
    { id: 'charges' as ReportTab, label: 'Cobranças', icon: <FileText size={18} /> },
    { id: 'export' as ReportTab, label: 'Exportar', icon: <Download size={18} /> },
  ];

  const periodOptions = [
    { value: '7', label: 'Últimos 7 dias' },
    { value: '30', label: 'Últimos 30 dias' },
    { value: '90', label: 'Últimos 90 dias' },
    { value: '365', label: 'Último ano' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Relatórios</h1>
          <p className="text-surface-400">Análise de desempenho</p>
        </div>
        <Select
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          options={periodOptions}
          className="w-48"
        />
      </div>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`
              flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap
              transition-colors
              ${activeTab === tab.id
                ? 'bg-primary-500 text-white'
                : 'bg-surface-800 text-surface-300 hover:bg-surface-700'
              }
            `}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Revenue Tab */}
      {activeTab === 'revenue' && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <DollarSign size={20} className="text-green-400" />
                </div>
                <div>
                  <p className="text-xs text-surface-400">Receita Total</p>
                  <p className="text-lg font-semibold text-white">
                    {revenueLoading ? '-' : formatCurrency(revenueData?.totalRevenue || 0)}
                  </p>
                </div>
              </div>
            </Card>
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <FileText size={20} className="text-blue-400" />
                </div>
                <div>
                  <p className="text-xs text-surface-400">Cobranças Pagas</p>
                  <p className="text-lg font-semibold text-white">
                    {revenueLoading ? '-' : revenueData?.paidCount || 0}
                  </p>
                </div>
              </div>
            </Card>
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary-500/20 flex items-center justify-center">
                  <BarChart3 size={20} className="text-primary-400" />
                </div>
                <div>
                  <p className="text-xs text-surface-400">Ticket Médio</p>
                  <p className="text-lg font-semibold text-white">
                    {revenueLoading ? '-' : formatCurrency(revenueData?.averageTicket || 0)}
                  </p>
                </div>
              </div>
            </Card>
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-accent-500/20 flex items-center justify-center">
                  <TrendingUp size={20} className="text-accent-400" />
                </div>
                <div>
                  <p className="text-xs text-surface-400">Crescimento</p>
                  <p className={`text-lg font-semibold ${
                    (revenueData?.growth || 0) >= 0 ? 'text-green-400' : 'text-red-400'
                  }`}>
                    {revenueLoading ? '-' : `${revenueData?.growth || 0}%`}
                  </p>
                </div>
              </div>
            </Card>
          </div>

          {/* Revenue Chart Placeholder */}
          <Card>
            <div className="p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Receita por Período</h3>
              <div className="h-64 flex items-center justify-center bg-surface-800/30 rounded-lg">
                <div className="text-center">
                  <BarChart3 size={48} className="mx-auto text-surface-600 mb-3" />
                  <p className="text-surface-400">Gráfico de receita</p>
                  <p className="text-xs text-surface-500 mt-1">Em desenvolvimento</p>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Customers Tab */}
      {activeTab === 'customers' && (
        <Card>
          <div className="p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Top Clientes</h3>
            
            {customersLoading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="flex items-center gap-4 p-3 rounded-lg bg-surface-800/50 animate-pulse">
                    <div className="w-8 h-8 rounded-full bg-surface-700" />
                    <div className="flex-1">
                      <div className="h-4 w-32 bg-surface-700 rounded" />
                    </div>
                    <div className="h-4 w-20 bg-surface-700 rounded" />
                  </div>
                ))}
              </div>
            ) : customersData?.customers?.length > 0 ? (
              <div className="space-y-2">
                {customersData.customers.map((customer: any, index: number) => (
                  <div
                    key={customer.id}
                    className="flex items-center gap-4 p-3 rounded-lg bg-surface-800/50"
                  >
                    <div className="w-8 h-8 rounded-full bg-primary-500/20 flex items-center justify-center text-sm font-semibold text-primary-400">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-white">{customer.name}</p>
                      <p className="text-xs text-surface-400">{customer.chargeCount} cobranças</p>
                    </div>
                    <span className="text-sm font-semibold text-green-400">
                      {formatCurrency(customer.totalPaid)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Users size={48} className="mx-auto text-surface-600 mb-3" />
                <p className="text-surface-400">Nenhum cliente no período</p>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Charges Tab */}
      {activeTab === 'charges' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <div className="p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Status das Cobranças</h3>
              
              {chargeLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-surface-800/50 animate-pulse">
                      <div className="h-4 w-24 bg-surface-700 rounded" />
                      <div className="h-4 w-16 bg-surface-700 rounded" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-green-500/10">
                    <span className="text-sm text-green-400">Pagas</span>
                    <span className="text-sm font-semibold text-white">{chargeStats?.paid || 0}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-blue-500/10">
                    <span className="text-sm text-blue-400">Ativas</span>
                    <span className="text-sm font-semibold text-white">{chargeStats?.active || 0}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-red-500/10">
                    <span className="text-sm text-red-400">Vencidas</span>
                    <span className="text-sm font-semibold text-white">{chargeStats?.expired || 0}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-surface-500/10">
                    <span className="text-sm text-surface-400">Canceladas</span>
                    <span className="text-sm font-semibold text-white">{chargeStats?.cancelled || 0}</span>
                  </div>
                </div>
              )}
            </div>
          </Card>

          <Card>
            <div className="p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Taxa de Conversão</h3>
              <div className="h-48 flex items-center justify-center bg-surface-800/30 rounded-lg">
                <div className="text-center">
                  <PieChart size={48} className="mx-auto text-surface-600 mb-3" />
                  <p className="text-2xl font-bold text-white">
                    {chargeLoading ? '-' : `${chargeStats?.conversionRate || 0}%`}
                  </p>
                  <p className="text-sm text-surface-400">das cobranças foram pagas</p>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Export Tab */}
      {activeTab === 'export' && (
        <Card>
          <div className="p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Exportar Relatórios</h3>
            <p className="text-surface-400 mb-6">
              Exporte seus dados para análise externa ou contabilidade.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                onClick={() => handleExport('pdf')}
                className="flex items-center gap-4 p-6 rounded-lg bg-surface-800/50 hover:bg-surface-800 transition-colors"
              >
                <div className="w-12 h-12 rounded-lg bg-red-500/20 flex items-center justify-center">
                  <FileText size={24} className="text-red-400" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-medium text-white">Relatório PDF</p>
                  <p className="text-xs text-surface-400">Relatório completo formatado</p>
                </div>
              </button>

              <button
                onClick={() => handleExport('csv')}
                className="flex items-center gap-4 p-6 rounded-lg bg-surface-800/50 hover:bg-surface-800 transition-colors"
              >
                <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <Download size={24} className="text-green-400" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-medium text-white">Planilha CSV</p>
                  <p className="text-xs text-surface-400">Dados para Excel/Sheets</p>
                </div>
              </button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

export default SupplierReports;
